/**
 * UI locale — shared constants + client-safe helpers.
 *
 * The user-visible language toggle (`LanguageSwitcher`, in the header on
 * desktop and inside `MobileMenu` below `lg`) writes the locale to TWO places:
 *
 *   1. `localStorage[et_lang]` — same-tab + cross-tab sync via `storage` event.
 *      Read by `useLocale()` (client hook in `hooks/use-locale.ts`).
 *   2. Cookie `et_lang` — readable by **server** components so they can render
 *      locale-aware text on the first paint (no client-hydration flash).
 *
 * Why both, why not pick one:
 *   - Cookie alone would require a cookie-write detour for every locale read
 *     in the client and would lose the cross-tab `storage` event.
 *   - localStorage alone is invisible to server components — every label
 *     would have to render English first, then re-render after hydration.
 *
 * Writing both costs a few extra bytes per toggle and keeps both sides
 * fast. The hook keeps reading localStorage as its primary source so
 * client renders stay synchronous.
 *
 * **Server-only** helpers (anything that reads `next/headers`) live in
 * `src/lib/locale-server.ts` so this module stays importable from client
 * components.
 */

import { routing, type AppLocale } from "@/i18n/routing";

// Re-export so consumers can import the type from the conceptual home
// (`@/lib/locale`) without having to know that the literal-union itself
// happens to live in `data/suggestions.ts` (the module that first needed
// it). New code should prefer this path; both work.
/**
 * ⭐ DERIVED FROM `routing.locales`, NOT RESTATED (2026-09-20).
 *
 * These were two hand-maintained lists that had to agree: `routing.locales`
 * decided which URLs exist, and a literal `"en" | "vi" | "fr"` union decided
 * what the label modules would accept. Adding a locale to one and not the
 * other is silent in both directions — an unrouted locale typechecks
 * everywhere, and a routed one renders English labels under a new URL with no
 * error. Deriving makes adding a locale a COMPILER event: every
 * `Record<Locale, …>` in the codebase stops compiling until it covers the new
 * key, which is the only mechanism that reaches the label maps.
 *
 * ⚠️ Eight files carried their own copy of that union and are now importing
 * this one. If you find another, import it rather than retyping it.
 */
export type Locale = AppLocale;

export const STORAGE_KEY = "et_lang";
export const LOCALES = routing.locales;
export const DEFAULT_LOCALE: Locale = "en";

/**
 * BCP-47 tag for the `Intl` APIs (`toLocaleDateString`, `Intl.DateTimeFormat`,
 * `Intl.ListFormat`, …).
 *
 * ⛔ Nine call sites used to spell this inline as `locale === "vi" ? "vi-VN" :
 * "en-GB"`, which silently hands ENGLISH DATES to every locale that is not
 * Vietnamese. That is the shape of the whole 2026-09-20 locale spread: it
 * typechecks, it renders, and it is wrong in a way no test can see. Keyed on
 * `Exclude<Locale, "en">`, so adding a locale to `routing.locales` fails this
 * map until it is answered.
 *
 * ⚠️ `english` is a parameter, not a constant, because the codebase genuinely
 * uses two English date formats: `en-GB` on the dashboards ("20 September
 * 2026") and `en-US` in bylines and article dates ("September 20, 2026").
 * Unifying them is a visible copy change and an owner decision, so this
 * preserves each call site's existing choice rather than quietly picking one.
 *
 * ⚠️ NOT for publishing a date as copy — see `promoDeadline` in `packages.ts`,
 * which is deliberately hand-written per locale because `vi-VN` formats
 * `31/8/2026` without the leading zero we publish.
 */
/**
 * `null` means "format as English", and it is an ANSWER, not a gap. A locale
 * whose UI is not really translated renders English prose, and a French date
 * inside an English paragraph is worse than an English one: `/fr` is the whole
 * tree today (`fr.json` is `{}`), which is why `bylines.test.ts` pins
 * `formatBylineDate("2026-09-17", "fr")` to "Sep 17". That test caught this
 * exact regression when the map first said `fr: "fr-FR"`.
 *
 * Flip a locale to a real tag in the SAME change that fills its message file,
 * beside `HREFLANG_LOCALES`.
 */
const INTL_TAG: Record<Exclude<Locale, "en">, string | null> = {
  vi: "vi-VN",
  fr: "fr-FR",
  // Flipped 2026-09-23 with the message files (both valid Intl tags).
  "zh-Hans": "zh-Hans",
  "zh-Hant": "zh-Hant",
};

// Takes `string`, not `Locale`, because most call sites hold a raw `params`
// value. The MAP is still keyed on `Exclude<Locale, "en">`, so a new locale
// still fails to compile until answered; only the lookup is tolerant.
export function intlLocale(locale: string, english: "en-GB" | "en-US" = "en-GB"): string {
  return (INTL_TAG as Record<string, string | null | undefined>)[locale] ?? english;
}

/**
 * A value per locale, with English required and everything else optional.
 *
 * ⭐ `en` is mandatory and the rest are not, ON PURPOSE. The house rule is that
 * an untranslated key falls back to English rather than erroring
 * (`i18n/request.ts` deep-merges `en.json` under every locale for exactly this
 * reason), so a partial table is the CORRECT state for a locale mid-rollout,
 * not a gap to be forced shut. Making this total would mean answering 25 call
 * sites with English values the moment a locale is added, which is the same
 * fallback written 25 times by hand.
 *
 * The keys are still checked against `Locale`, so a typo (`{ en, iv }`) fails
 * to compile rather than silently never matching.
 */
export type LocaleTable<T> = { en: T } & Partial<Record<Exclude<Locale, "en">, T>>;

/** The locale's entry, or English. Takes `string` because call sites hold a raw
 *  `params` value; the TABLE is what stays typed. */
export function byLocale<T>(locale: string, table: LocaleTable<T>): T {
  return (table as Record<string, T | undefined>)[locale] ?? table.en;
}

/** Per-locale label overrides on top of an English base. */
export type LabelOverrides = Partial<
  Record<Exclude<Locale, "en">, Readonly<Record<string, string>>>
>;

/**
 * The locale's label for `key`, falling back to `english`.
 *
 * This is the other half of the spread: `locale === "vi" ? MAP_VI[k] ?? en : en`
 * appeared eleven times, and every one of them handed English to every locale
 * that was not Vietnamese. Here the fallback is the DECLARED behaviour rather
 * than an accident of the ternary's else branch.
 */
export function localeLabel(
  locale: string,
  key: string,
  english: string,
  overrides: LabelOverrides,
): string {
  return (overrides as Record<string, Readonly<Record<string, string>> | undefined>)[locale]?.[key]
    ?? english;
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/**
 * Write the locale to localStorage AND the document cookie, then dispatch
 * the same-tab `et_lang_change` event so `useLocale()` consumers update
 * without a reload. Cookie lifetime is 1 year — long enough that returning
 * users don't see English-by-default after a brief absence.
 *
 * Client-only — guarded against SSR by the `document` check, but callers
 * should already be inside a `"use client"` boundary.
 */
export function setLocaleClient(code: Locale): void {
  if (typeof document === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Private mode / storage quota — fall through to cookie write so the
    // server-rendered view at least flips on the next request.
  }
  // SameSite=Lax + Path=/ so the cookie is sent on top-level nav from
  // anywhere on the site. No `Secure` because dev runs over http; modern
  // browsers permit Lax-without-Secure for same-origin sub-resource sends.
  document.cookie = `${STORAGE_KEY}=${code}; Max-Age=${60 * 60 * 24 * 365}; Path=/; SameSite=Lax`;
  // Also sync next-intl's NEXT_LOCALE cookie (URL-locale source of truth) so
  // the locale middleware agrees with the data-label locale. Without this,
  // switching back to the default (unprefixed) locale gets bounced to "/" and
  // the stale NEXT_LOCALE cookie redirects right back ("stuck on VI"). Same
  // attrs as next-intl's default cookie.
  document.cookie = `NEXT_LOCALE=${code}; Max-Age=${60 * 60 * 24 * 365}; Path=/; SameSite=Lax`;
  window.dispatchEvent(new Event("et_lang_change"));
}

/**
 * Read the locale cookie from `document.cookie` on the client. Returns
 * `null` if absent. Used by the migration write in `useLocale` — if a
 * returning user has localStorage but no cookie (pre-2026-05-14 state),
 * we mirror the value into the cookie on first read so server renders
 * pick it up on the next request.
 */
export function readLocaleCookieClient(): Locale | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${STORAGE_KEY}=([^;]+)`));
  if (!match) return null;
  return isLocale(match[1]) ? match[1] : null;
}
